/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  BENEFIT_BASE_URL,
  DISTRIBUTION_LIST_BASE_URL,
  PROFILE_BASE_URL,
  PROFILE_BLOCK_SHIPPING_BASE_URL,
  PROGRAM_PROFILE_BASE_URL,
} from "Constants/SagaConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  IProgramDetailsDataResult,
  IProgramDetailsRequest,
} from "Dataflows/UserProfile/UserProfileSaga";
import { IClaimBenefitDataResult } from "Interfaces/Benefits/IClaimBenefitDataResult";
import { IProfileBenefitDataResult } from "Interfaces/Benefits/IProfileBenefitDataResult";
import { IApiEmptyResult, IApiResult2 } from "Interfaces/IApiResult";
import { IDistributionListsDataResult } from "Interfaces/UserProfile/DistributionLists/IDistributionListsDataResult";
import { INewUserProfileCommentRequest } from "Interfaces/UserProfile/INewUserProfileCommentRequest";
import { IProgramProfileDataResult } from "Interfaces/UserProfile/IProgramProfileDataResult";
import { IUserProfileBlockShippingDataResult } from "Interfaces/UserProfile/IUserProfileBlockShippingDataResult";
import { IUserProfileBlockShippingRequest } from "Interfaces/UserProfile/IUserProfileBlockShippingRequest";
import { IUserProfileCommentDataResult } from "Interfaces/UserProfile/IUserProfileCommentDataResult";
import { IUserProfileCommentRequest } from "Interfaces/UserProfile/IUserProfileCommentRequest";
import { IUserProfileCommentsDataResult } from "Interfaces/UserProfile/IUserProfileCommentsDataResult";
import { IUserProfileDataResult } from "Interfaces/UserProfile/IUserProfileDataResult";
import { IUserProfileRequest } from "Interfaces/UserProfile/IUserProfileRequest";
import { IUserProfilesSearchDataResult } from "Interfaces/UserProfile/IUserProfilesSearchDataResult";
import { IUserProfilesSearchRequest } from "Interfaces/UserProfile/IUserProfilesSearchRequest";
import { IUserProfileVSBenefitsDataResult } from "Interfaces/UserProfile/IUserProfileVSBenefitsDataResult";

import { get, post, put, remove, IHttpResponse } from "./ApiUtils";

/**
 * Updates the users Program Details.
 * @param programDetailsRequest - the users program details to update.
 */
export async function updateProgramProfile(
  programDetailsRequest: IProgramDetailsRequest
): Promise<IHttpResponse<IApiResult2<IProgramDetailsDataResult>>> {
  return await put<IApiResult2<IProgramDetailsDataResult>>(
    PROGRAM_PROFILE_BASE_URL,
    programDetailsRequest,
    true
  );
}

/**
 * Get the user profile.
 * @param userProfileId - The profile id
 */
export async function getUserProfile(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IUserProfileDataResult>>> {
  return await get<IApiResult2<IUserProfileDataResult>>(PROFILE_BASE_URL + userProfileId, true);
}

/**
 * Get the user profile by email address.
 * @param email - The email address.
 */
export async function getUserProfileByEmail(
  email: string
): Promise<IHttpResponse<IApiResult2<IUserProfileDataResult>>> {
  return await get<IApiResult2<IUserProfileDataResult>>(PROFILE_BASE_URL + email, true);
}

/**
 * Update the user profile.
 * @param userProfile - The request object.
 */
export async function updateUserProfile(
  userProfile: IUserProfileRequest
): Promise<IHttpResponse<IApiResult2<IUserProfileDataResult>>> {
  return await put<IApiResult2<IUserProfileDataResult>>(PROFILE_BASE_URL, userProfile, true);
}

/**
 * Update the user profile block shipping field.
 * @param userProfile - The request object.
 */
export async function updateUserProfileBlockShipping(
  blockShippingRequest: IUserProfileBlockShippingRequest
): Promise<IHttpResponse<IApiResult2<IUserProfileBlockShippingDataResult>>> {
  return await put<IApiResult2<IUserProfileBlockShippingDataResult>>(
    PROFILE_BLOCK_SHIPPING_BASE_URL,
    blockShippingRequest,
    true
  );
}

/**
 * Search all the user profiles for entered text.
 * @param searchRequest - The search request
 */
export async function searchUserProfiles(
  searchRequest: IUserProfilesSearchRequest
): Promise<IHttpResponse<IApiResult2<IUserProfilesSearchDataResult>>> {
  return await post<IApiResult2<IUserProfilesSearchDataResult>>(
    PROFILE_BASE_URL + "search",
    searchRequest,
    true
  );
}

/**
 * Get comments for the user profile.
 * @param userProfileId - The profile id
 */
export async function getUserProfileComments(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IUserProfileCommentsDataResult>>> {
  return await get<IApiResult2<IUserProfileCommentsDataResult>>(
    PROFILE_BASE_URL + userProfileId + "/Comments",
    true
  );
}

/**
 * Create the user profile comment.
 * @param userProfileId number
 * @param userProfileComment - The comment to create
 */
export async function createUserProfileComment(
  userProfileId: number,
  newUserProfileComment: INewUserProfileCommentRequest
): Promise<IHttpResponse<IApiResult2<IUserProfileCommentDataResult>>> {
  return await post<IApiResult2<IUserProfileCommentDataResult>>(
    PROFILE_BASE_URL + userProfileId + "/Comments",
    newUserProfileComment,
    true
  );
}

/**
 * Update a user profile comment.
 * @param userProfileId number
 * @param userProfileComment - The comment to update
 */
export async function updateUserProfileComment(
  userProfileId: number,
  userProfileComment: IUserProfileCommentRequest
): Promise<IHttpResponse<IApiResult2<IUserProfileCommentDataResult>>> {
  return await put<IApiResult2<IUserProfileCommentDataResult>>(
    PROFILE_BASE_URL + userProfileId + "/Comments",
    userProfileComment,
    true
  );
}

/**
 * Get benefits for the user profile.
 * @param userProfileId - The profile id
 */
export async function getUserProfileVSBenefits(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IUserProfileVSBenefitsDataResult>>> {
  return await get<IApiResult2<IUserProfileVSBenefitsDataResult>>(
    PROFILE_BASE_URL + userProfileId + "/Benefits/VsBenefit",
    true
  );
}

/**
 * Provision the user's benefit
 * @param userProfileId - The profile id
 */
export async function provisionUserProfileVSBenefits(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IUserProfileVSBenefitsDataResult>>> {
  return await post<IApiResult2<IUserProfileVSBenefitsDataResult>>(
    PROFILE_BASE_URL + userProfileId + "/Benefits/ProvisionVsBenefit",
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Extend the user's benefit
 * @param userProfileId - The profile id
 */
export async function extendUserProfileVSBenefits(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IUserProfileVSBenefitsDataResult>>> {
  return await post<IApiResult2<IUserProfileVSBenefitsDataResult>>(
    PROFILE_BASE_URL + userProfileId + "/Benefits/ExtendVsBenefit",
    { userProfileId: userProfileId },
    true
  );
}

/**
 * End the user's benefit
 * @param userProfileId - The profile id
 */
export async function endUserProfileVSBenefits(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IUserProfileVSBenefitsDataResult>>> {
  return await post<IApiResult2<IUserProfileVSBenefitsDataResult>>(
    PROFILE_BASE_URL + userProfileId + "/Benefits/EndVsBenefit",
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the github pro benefit for the user profile.
 * @param {number} userProfileId The profile id.
 * @param {string} tenant The selected tenant.
 * @returns {IClaimBenefitDataResult}
 */
export async function claimUserProfileBenefitsGithubPro(
  userProfileId: number,
  tenant: string
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", tenant.toLowerCase())}github`,
    { userProfileId: userProfileId },
    true
  );
}

/**
 * Claims the SwagKit benefit for the user profile.
 * @param {number} userProfileId The profile id.
 * @param {string} swagKitType  The selected swag-kit.
 * @returns {IClaimBenefitDataResult}
 */
export async function claimUserProfileBenefitSwagkit(
  userProfileId: number,
  swagKitType: string
): Promise<IHttpResponse<IApiResult2<IClaimBenefitDataResult>>> {
  return await post<IApiResult2<IClaimBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MLSA.toLowerCase())}swagkit`,
    { userProfileId: userProfileId, swagKitType: swagKitType },
    true
  );
}

/**
 * Gets the profile benefits from the api for the user profileid.
 * @param {number} userProfileId The profile id.
 * @param {string} tenant The selected tenant.
 * @returns {IProfileBenefitDataResult}
 */
export async function getProfileBenefits(
  userProfileId: number
): Promise<IHttpResponse<IApiResult2<IProfileBenefitDataResult>>> {
  return await get<IApiResult2<IProfileBenefitDataResult>>(
    `${BENEFIT_BASE_URL.replace("{tenant}", TenantTypesEnum.MLSA.toLowerCase())}${userProfileId}`,
    true
  );
}

export async function fetchProgramProfile(
  programProfileId: number
): Promise<IHttpResponse<IApiResult2<IProgramProfileDataResult>>> {
  return await get<IApiResult2<IProgramProfileDataResult>>(
    PROGRAM_PROFILE_BASE_URL + programProfileId,
    true
  );
}

/**
 * Load the user profile's dl.
 * @param userProfileId number
 * @param tenant string
 */
export async function loadDistributionLists(
  userProfileId: number,
  tenant: string
): Promise<IHttpResponse<IApiResult2<IDistributionListsDataResult>>> {
  return await get<IApiResult2<IDistributionListsDataResult>>(
    `${DISTRIBUTION_LIST_BASE_URL.replace("{tenant}", tenant.toLocaleLowerCase())}${userProfileId}`,
    true
  );
}

/**
 * Remove the user profile's dl.
 * @param dlGroup string
 * @param userEmail string
 * @param tenant string
 */
export async function removeDistributionLists(
  dlGroup: string,
  userEmail: string,
  tenant: string
): Promise<IHttpResponse<IApiResult2<IApiEmptyResult>>> {
  return await remove<IApiResult2<IApiEmptyResult>>(
    `${DISTRIBUTION_LIST_BASE_URL.replace("{tenant}", tenant.toLocaleLowerCase())}email`,
    { distributionGroup: dlGroup, userEmail },
    true
  );
}

/**
 * Remove the user profile's dl.
 * @param dlGroup string
 * @param userEmail string
 * @param tenant string
 */
export async function removeAllDistributionLists(
  userProfileId: number,
  tenant: string
): Promise<IHttpResponse<IApiResult2<IApiEmptyResult>>> {
  return await remove<IApiResult2<IApiEmptyResult>>(
    `${DISTRIBUTION_LIST_BASE_URL.replace(
      "{tenant}",
      tenant.toLocaleLowerCase()
    )}contactFromTenant`,
    { userProfileId },
    true
  );
}

/**
 * Updates the user profile user flag.
 * @param userProfileId The user profile id.
 * @param isPrivate The user profile flag.
 */
export async function updateUserProfileIsPrivate(
  userProfileId: number,
  isPrivate: boolean
): Promise<IHttpResponse<IApiResult2<IApiEmptyResult>>> {
  return await post<IApiResult2<IApiEmptyResult>>(
    `${PROFILE_BASE_URL}MarkPrivate`,
    { userProfileId, isPrivate },
    true
  );
}
